/**
 * 分组服务（Web 应用版本）
 * 提供分组创建、成员管理等功能
 */

import { supabase, inviteMember as sharedInviteMember, acceptInvitation as sharedAcceptInvitation, rejectInvitation as sharedRejectInvitation, getPendingInvitations as sharedGetPendingInvitations } from '@ext-bookmark/shared/api';
import { getCurrentUser } from '../auth/userService';
import type { Group, GroupMemberWithProfile, GroupServiceError, GroupInvitation, GroupMemberRole, InvitationStatus } from '@ext-bookmark/shared/types';
import * as groupCache from './groupCache';
import { clearLastUsedGroupId, getLastUsedGroupId } from './lastUsedGroup';
import { createNotification } from '../notifications/notificationService';
import { getWithCache } from '../cache/cacheStrategy';
import { getCache } from '../cache/cacheManager';

// 正在进行的个人分组创建请求（防止重复请求）
const pendingPersonalGroupRequests: Map<string, Promise<{ group: Group | null; error: GroupServiceError | null }>> = new Map();

/**
 * 确保用户有个人分组（如果不存在则创建）
 * 使用请求锁机制防止并发请求导致的重复创建
 */
export async function ensurePersonalGroup(userId: string): Promise<{ group: Group | null; error: GroupServiceError | null }> {
  // 如果已有相同 userId 的请求在进行，等待该请求完成并返回结果
  const existingRequest = pendingPersonalGroupRequests.get(userId);
  if (existingRequest) {
    return existingRequest;
  }

  // 创建新的请求 Promise
  const requestPromise = (async (): Promise<{ group: Group | null; error: GroupServiceError | null }> => {
    if (!supabase) {
      return {
        group: null,
        error: { message: 'Supabase 未配置' },
      };
    }

    try {
      // 检查是否已有个人分组（使用 maybeSingle 避免多条记录时报错）
      const { data: existingGroups, error: checkError } = await supabase
        .from('groups')
        .select('*')
        .eq('created_by', userId)
        .eq('is_personal', true);

      if (checkError) {
        console.error('[Group] 查询个人分组失败:', checkError);
        return {
          group: null,
          error: {
            message: checkError.message || '查询个人分组失败',
            code: checkError.code,
          },
        };
      }

      // 如果已存在个人分组，返回第一个（理论上应该只有一个）
      if (existingGroups && existingGroups.length > 0) {
        const existingGroup = existingGroups[0];
        // 已存在个人分组，返回它
        const group: Group = {
          id: existingGroup.id,
          name: existingGroup.name,
          createdBy: existingGroup.created_by,
          createdAt: existingGroup.created_at,
          updatedAt: existingGroup.updated_at,
          isPersonal: existingGroup.is_personal,
          isDeletable: existingGroup.is_deletable,
          color: existingGroup.color || undefined,
        };

        // 确保用户是自己的个人分组成员
        const { data: memberData } = await supabase
          .from('group_members')
          .select('id')
          .eq('group_id', existingGroup.id)
          .eq('user_id', userId)
          .single();

        if (!memberData) {
          // 添加用户为自己的成员（角色为 owner）
          await supabase
            .from('group_members')
            .insert({
              group_id: existingGroup.id,
              user_id: userId,
              role: 'owner',
              joined_at: new Date().toISOString(),
            });
          // 清除成员列表缓存
          await groupCache.clearGroupMembersCache(existingGroup.id);
        }

        return { group, error: null };
      }

      // 创建个人分组
      const now = new Date().toISOString();
      const { data: newGroup, error: createError } = await supabase
        .from('groups')
        .insert({
          name: '个人',
          created_by: userId,
          created_at: now,
          updated_at: now,
          is_personal: true,
          is_deletable: false,
          color: null, // 个人分组默认无颜色
        })
        .select()
        .single();

      if (createError) {
        // 处理唯一约束冲突错误（PostgreSQL 错误代码 23505）
        // 这表示个人分组已存在（可能是并发请求创建的）
        if (createError.code === '23505' || 
            createError.message?.includes('duplicate') || 
            createError.message?.includes('unique') ||
            createError.message?.includes('idx_groups_unique_personal')) {
          console.warn('[Group] 个人分组已存在（可能是并发创建），重新查询:', createError.message);
          
          // 重新查询已存在的个人分组
          const { data: existingGroupsRetry, error: retryError } = await supabase
            .from('groups')
            .select('*')
            .eq('created_by', userId)
            .eq('is_personal', true)
            .limit(1)
            .single();

          if (retryError || !existingGroupsRetry) {
            console.error('[Group] 重新查询个人分组失败:', retryError);
            return {
              group: null,
              error: {
                message: retryError?.message || '查询个人分组失败',
                code: retryError?.code,
              },
            };
          }

          // 返回已存在的个人分组
          const group: Group = {
            id: existingGroupsRetry.id,
            name: existingGroupsRetry.name,
            createdBy: existingGroupsRetry.created_by,
            createdAt: existingGroupsRetry.created_at,
            updatedAt: existingGroupsRetry.updated_at,
            isPersonal: existingGroupsRetry.is_personal,
            isDeletable: existingGroupsRetry.is_deletable,
            color: existingGroupsRetry.color || undefined,
          };

          // 确保用户是自己的个人分组成员
          const { data: memberData } = await supabase
            .from('group_members')
            .select('id')
            .eq('group_id', existingGroupsRetry.id)
            .eq('user_id', userId)
            .single();

          if (!memberData) {
            // 添加用户为自己的成员（角色为 owner）
            await supabase
              .from('group_members')
              .insert({
                group_id: existingGroupsRetry.id,
                user_id: userId,
                role: 'owner',
                joined_at: new Date().toISOString(),
              });
            // 清除成员列表缓存
            await groupCache.clearGroupMembersCache(existingGroupsRetry.id);
          }

          return { group, error: null };
        }

        // 其他错误
        console.error('[Group] 创建个人分组失败:', createError);
        return {
          group: null,
          error: {
            message: createError.message || '创建个人分组失败',
            code: createError.code,
          },
        };
      }

      // 自动将创建者添加为分组成员（角色为 owner）
      const { error: memberError } = await supabase
        .from('group_members')
        .insert({
          group_id: newGroup.id,
          user_id: userId,
          role: 'owner',
          joined_at: now,
        });

      if (memberError) {
        console.error('[Group] 添加创建者为成员失败:', memberError);
      }

      const group: Group = {
        id: newGroup.id,
        name: newGroup.name,
        createdBy: newGroup.created_by,
        createdAt: newGroup.created_at,
        updatedAt: newGroup.updated_at,
        isPersonal: newGroup.is_personal,
        isDeletable: newGroup.is_deletable,
      };

      // 清除分组列表缓存，因为新增了个人分组
      await groupCache.clearUserGroupsCache();

      return { group, error: null };
    } catch (error: any) {
      console.error('[Group] 确保个人分组异常:', error);
      return {
        group: null,
        error: {
          message: error.message || '确保个人分组时发生未知错误',
        },
      };
    }
  })();

  // 保存请求 Promise
  pendingPersonalGroupRequests.set(userId, requestPromise);

  try {
    const result = await requestPromise;
    return result;
  } finally {
    // 请求完成后移除
    pendingPersonalGroupRequests.delete(userId);
  }
}

/**
 * 创建分组
 */
export async function createGroup(name: string): Promise<{ group: Group | null; error: GroupServiceError | null }> {
  if (!supabase) {
    return {
      group: null,
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      group: null,
      error: { message: '用户未登录' },
    };
  }

  try {
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('groups')
      .insert({
        name,
        created_by: user.id,
        created_at: now,
        updated_at: now,
        is_personal: false,
        is_deletable: true,
      })
      .select()
      .single();

    if (error) {
      console.error('[Group] 创建分组失败:', error);
      return {
        group: null,
        error: {
          message: error.message || '创建分组失败',
          code: error.code,
        },
      };
    }

    // 自动将创建者添加为分组成员（角色为 owner）
    const { error: memberError } = await supabase
      .from('group_members')
      .insert({
        group_id: data.id,
        user_id: user.id,
        role: 'owner',
        joined_at: now,
      });

    if (memberError) {
      console.error('[Group] 添加创建者为成员失败:', memberError);
    }

    const group: Group = {
      id: data.id,
      name: data.name,
      createdBy: data.created_by,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      isPersonal: data.is_personal,
      isDeletable: data.is_deletable,
      color: data.color || undefined,
    };

    // 清除分组列表缓存，因为新增了分组
    await groupCache.clearUserGroupsCache();

    return { group, error: null };
  } catch (error: any) {
    console.error('[Group] 创建分组异常:', error);
    return {
      group: null,
      error: {
        message: error.message || '创建分组时发生未知错误',
      },
    };
  }
}

/** 可选参数：noCache 为 true 时跳过 getWithCache，供 React Query queryFn 使用 */
export type GetUserGroupsOptions = { noCache?: boolean };

/**
 * 获取用户所属的所有分组（包括个人分组）
 * noCache 时纯拉取（供 React Query 使用）
 */
export async function getUserGroups(
  onUpdate?: (groups: Group[]) => void,
  options?: GetUserGroupsOptions
): Promise<{ groups: Group[]; error: GroupServiceError | null }> {
  if (!supabase) {
    return { groups: [], error: { message: 'Supabase 未配置' } };
  }
  const user = await getCurrentUser();
  if (!user) {
    return { groups: [], error: { message: '用户未登录' } };
  }

  const fetchGroups = async (): Promise<Group[]> => {
    if (!supabase) throw new Error('Supabase 未配置');
    const { data, error } = await supabase
      .from('group_members')
      .select(`
        group_id,
        groups (
          id,
          name,
          created_by,
          created_at,
          updated_at,
          is_personal,
          is_deletable,
          color
        )
      `)
      .eq('user_id', user.id);
    if (error) throw new Error(error.message || '获取分组列表失败');
    return (data || [])
      .map((item: any) => ({
        id: item.groups.id,
        name: item.groups.name,
        createdBy: item.groups.created_by,
        createdAt: item.groups.created_at,
        updatedAt: item.groups.updated_at,
        isPersonal: item.groups.is_personal,
        isDeletable: item.groups.is_deletable,
        color: item.groups.color || undefined,
      }))
      .sort((a: Group, b: Group) => {
        if (a.isPersonal && !b.isPersonal) return -1;
        if (!a.isPersonal && b.isPersonal) return 1;
        return a.name.localeCompare(b.name);
      });
  };

  if (options?.noCache) {
    try {
      const groups = await fetchGroups();
      return { groups, error: null };
    } catch (error: any) {
      return { groups: [], error: { message: error.message || '获取分组列表时发生未知错误' } };
    }
  }

  const cacheKey = 'user_groups_cache';
  try {
    const groups = await getWithCache<Group[]>(cacheKey, fetchGroups, onUpdate);
    return { groups, error: null };
  } catch (error: any) {
    const cached = await getCache<Group[]>(cacheKey);
    if (cached) {
      if (onUpdate) onUpdate(cached.data);
      return { groups: cached.data, error: null };
    }
    return { groups: [], error: { message: error.message || '获取分组列表时发生未知错误' } };
  }
}

export type GetGroupMembersOptions = { noCache?: boolean };

/**
 * 获取分组成员列表
 * noCache 时纯拉取（供 React Query 使用）
 */
export async function getGroupMembers(
  groupId: string,
  onUpdate?: (members: GroupMemberWithProfile[]) => void,
  options?: GetGroupMembersOptions
): Promise<{ members: GroupMemberWithProfile[]; error: GroupServiceError | null }> {
  if (!supabase) {
    return { members: [], error: { message: 'Supabase 未配置' } };
  }
  const user = await getCurrentUser();
  if (!user) {
    return { members: [], error: { message: '用户未登录' } };
  }

  const fetchMembers = async (): Promise<GroupMemberWithProfile[]> => {
    if (!supabase) throw new Error('Supabase 未配置');
    const { data: membersData, error: membersError } = await supabase
      .from('group_members')
      .select('id, group_id, user_id, role, joined_at')
      .eq('group_id', groupId);
    if (membersError) throw new Error(membersError.message || '获取分组成员失败');
    if (!membersData || membersData.length === 0) return [];
    const userIds = membersData.map((m: any) => m.user_id);
    const [profilesResult, emailsResult] = await Promise.allSettled([
      supabase.from('profiles').select('id, username').in('id', userIds),
      supabase.rpc('get_user_emails_by_ids', { user_ids: userIds }),
    ]);
    let profilesData: any[] = [];
    if (profilesResult.status === 'fulfilled' && profilesResult.value.data) {
      profilesData = profilesResult.value.data;
    }
    let emailsData: any[] = [];
    if (emailsResult.status === 'fulfilled' && emailsResult.value.data) {
      emailsData = emailsResult.value.data;
    }
    const profileMap = new Map((profilesData || []).map((p: any) => [p.id, { username: p.username }]));
    const emailMap = new Map<string, string>((emailsData || []).map((e: any) => [e.user_id, e.email || '']));
    return membersData.map((item: any) => {
      const profile = profileMap.get(item.user_id) || { username: '' };
      const email: string = emailMap.get(item.user_id) ?? '';
      let role: GroupMemberRole = 'member';
      if (item.role !== undefined && item.role !== null) role = item.role as GroupMemberRole;
      return {
        id: item.id,
        groupId: item.group_id,
        userId: item.user_id,
        role,
        joinedAt: item.joined_at,
        username: profile.username,
        email,
      };
    });
  };

  if (options?.noCache) {
    try {
      const members = await fetchMembers();
      return { members, error: null };
    } catch (error: any) {
      return { members: [], error: { message: error.message || '获取分组成员时发生未知错误' } };
    }
  }

  const cacheKey = `group_members_cache_${groupId}`;
  try {
    const members = await getWithCache<GroupMemberWithProfile[]>(cacheKey, fetchMembers, onUpdate);
    return { members, error: null };
  } catch (error: any) {
    const cached = await getCache<GroupMemberWithProfile[]>(cacheKey);
    if (cached) {
      if (onUpdate) onUpdate(cached.data);
      return { members: cached.data, error: null };
    }
    return { members: [], error: { message: error.message || '获取分组成员时发生未知错误' } };
  }
}

/**
 * 更新分组名称
 */
export async function updateGroupName(groupId: string, newName: string): Promise<{ error: GroupServiceError | null }> {
  if (!supabase) {
    return {
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      error: { message: '用户未登录' },
    };
  }

  try {
    const { error } = await supabase
      .from('groups')
      .update({
        name: newName,
        updated_at: new Date().toISOString(),
      })
      .eq('id', groupId);

    if (error) {
      console.error('[Group] 更新分组名称失败:', {
        error: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        groupId,
        newName,
      });
      return {
        error: {
          message: error.message || '更新分组名称失败',
          code: error.code,
        },
      };
    }

    // 清除分组列表缓存，因为分组信息已更新
    await groupCache.clearUserGroupsCache();

    return { error: null };
  } catch (error: any) {
    console.error('[Group] 更新分组名称异常:', error);
    return {
      error: {
        message: error.message || '更新分组名称时发生未知错误',
      },
    };
  }
}

/**
 * 更新分组颜色
 */
export async function updateGroupColor(groupId: string, color: string | null): Promise<{ error: GroupServiceError | null }> {
  if (!supabase) {
    return {
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      error: { message: '用户未登录' },
    };
  }

  try {
    const { error } = await supabase
      .from('groups')
      .update({
        color: color,
        updated_at: new Date().toISOString(),
      })
      .eq('id', groupId);

    if (error) {
      console.error('[Group] 更新分组颜色失败:', error);
      return {
        error: {
          message: error.message || '更新分组颜色失败',
          code: error.code,
        },
      };
    }

    // 清除分组列表缓存，因为分组信息已更新
    await groupCache.clearUserGroupsCache();

    return { error: null };
  } catch (error: any) {
    console.error('[Group] 更新分组颜色异常:', error);
    return {
      error: {
        message: error.message || '更新分组颜色时发生未知错误',
      },
    };
  }
}

/**
 * 邀请成员（通过邮箱）
 * 创建邀请记录，不直接添加成员，需要被邀请者同意
 */
export async function inviteMember(groupId: string, email: string): Promise<{ invitation: GroupInvitation | null; error: GroupServiceError | null }> {
  return sharedInviteMember(groupId, email.trim().toLowerCase(), {
    onInviteCreated: async ({ invitation }) => {
      await createNotification(
        invitation.inviteeId,
        'group_invite',
        '分组邀请',
        `${invitation.inviterName || '用户'} 邀请您加入分组"${invitation.groupName || '分组'}"`,
        {
          group_id: invitation.groupId,
          inviter_id: invitation.inviterId,
          invitation_id: invitation.id,
          expires_at: invitation.expiresAt,
        }
      );
    },
  });
}

/**
 * 移除成员
 */
export async function removeMember(groupId: string, userId: string): Promise<{ error: GroupServiceError | null }> {
  if (!supabase) {
    return {
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      error: { message: '用户未登录' },
    };
  }

  try {
    // 检查权限
    const { hasPermission, error: permError } = await checkPermission(groupId, 'remove');
    if (permError || !hasPermission) {
      // 如果用户是自己移除自己（离开分组），允许
      if (userId !== user.id) {
        return {
          error: { message: '只有创建者和管理员可以移除其他成员' },
        };
      }
    }

    // 检查目标成员是否是创建者
    const { members } = await getGroupMembers(groupId);
    const targetMember = members.find(m => m.userId === userId);
    if (targetMember?.role === 'owner' && userId !== user.id) {
      return {
        error: { message: '不能移除创建者' },
      };
    }

    const { error } = await supabase
      .from('group_members')
      .delete()
      .eq('group_id', groupId)
      .eq('user_id', userId);

    if (error) {
      console.error('[Group] 移除成员失败:', error);
      return {
        error: {
          message: error.message || '移除成员失败',
          code: error.code,
        },
      };
    }

    // 获取分组信息（用于通知）
    const { data: groupData } = await supabase
      .from('groups')
      .select('name')
      .eq('id', groupId)
      .single();

    const groupName = groupData?.name || '分组';

    // 获取移除者信息（用于通知）
    const { data: removerProfile } = await supabase
      .from('profiles')
      .select('username')
      .eq('id', user.id)
      .single();

    const removerName = removerProfile?.username || '用户';

    // 创建通知（通知被移除的用户）
    await createNotification(
      userId,
      'group_member_removed',
      '被移出分组',
      `${removerName} 将您移出了分组"${groupName}"`,
      {
        group_id: groupId,
        remover_id: user.id,
      }
    ).catch((err) => {
      // 通知创建失败不影响移除流程，只记录错误
      console.error('[Group] 创建成员移除通知失败:', err);
    });

    // 清除该分组的成员列表缓存，因为成员已更新
    await groupCache.clearGroupMembersCache(groupId);

    return { error: null };
  } catch (error: any) {
    console.error('[Group] 移除成员异常:', error);
    return {
      error: {
        message: error.message || '移除成员时发生未知错误',
      },
    };
  }
}

/**
 * 离开分组
 */
export async function leaveGroup(groupId: string): Promise<{ error: GroupServiceError | null }> {
  if (!supabase) {
    return {
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      error: { message: '用户未登录' },
    };
  }

  // 检查是否是个人分组
  const { data: groupData } = await supabase
    .from('groups')
    .select('is_personal')
    .eq('id', groupId)
    .single();

  if (groupData?.is_personal) {
    return {
      error: { message: '不能离开个人分组' },
    };
  }

  return removeMember(groupId, user.id);
}

/**
 * 删除分组
 */
export async function deleteGroup(groupId: string): Promise<{ error: GroupServiceError | null }> {
  if (!supabase) {
    return {
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      error: { message: '用户未登录' },
    };
  }

  try {
    // 检查分组是否存在且可删除
    const { data: groupData, error: groupError } = await supabase
      .from('groups')
      .select('is_deletable, created_by')
      .eq('id', groupId)
      .single();

    if (groupError || !groupData) {
      return {
        error: {
          message: '分组不存在',
          code: groupError?.code,
        },
      };
    }

    if (!groupData.is_deletable) {
      return {
        error: { message: '个人分组不能删除' },
      };
    }

    // 删除分组（会自动删除所有分组成员和关联的待办事项的group_id）
    const { error } = await supabase
      .from('groups')
      .delete()
      .eq('id', groupId);

    if (error) {
      console.error('[Group] 删除分组失败:', error);
      return {
        error: {
          message: error.message || '删除分组失败',
          code: error.code,
        },
      };
    }

    // 清除分组列表缓存和该分组的成员列表缓存
    await groupCache.clearUserGroupsCache();
    await groupCache.clearGroupMembersCache(groupId);

    // 如果删除的是最近使用的分组，清除最近使用的分组记录
    try {
      const lastUsedGroupId = await getLastUsedGroupId();
      if (lastUsedGroupId === groupId) {
        await clearLastUsedGroupId();
      }
    } catch (error) {
      console.warn('[Group] 清除最近使用的分组失败:', error);
    }

    return { error: null };
  } catch (error: any) {
    console.error('[Group] 删除分组异常:', error);
    return {
      error: {
        message: error.message || '删除分组时发生未知错误',
      },
    };
  }
}

/**
 * 接受邀请
 */
export async function acceptInvitation(invitationId: string): Promise<{ member: GroupMemberWithProfile | null; error: GroupServiceError | null }> {
  return sharedAcceptInvitation(invitationId, {
    onAccepted: async ({ invitation, groupName, inviteeName }) => {
      await groupCache.clearGroupMembersCache(invitation.group_id);
      await createNotification(
        invitation.inviter_id,
        'group_invite_accepted',
        '邀请已接受',
        `${inviteeName || '用户'} 已接受您对分组"${groupName || '分组'}"的邀请`,
        {
          group_id: invitation.group_id,
          invitee_id: invitation.invitee_id,
        }
      );
    },
  });
}

/**
 * 拒绝邀请
 */
export async function rejectInvitation(invitationId: string): Promise<{ error: GroupServiceError | null }> {
  return sharedRejectInvitation(invitationId);
}

/**
 * 获取待处理的邀请列表（当前用户收到的）
 */
export async function getPendingInvitations(): Promise<{ invitations: GroupInvitation[]; error: GroupServiceError | null }> {
  return sharedGetPendingInvitations();
}

/**
 * 获取分组的所有邀请（包括已处理）
 */
export async function getGroupInvitations(groupId: string): Promise<{ invitations: GroupInvitation[]; error: GroupServiceError | null }> {
  if (!supabase) {
    return {
      invitations: [],
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      invitations: [],
      error: { message: '用户未登录' },
    };
  }

  try {
    // 检查用户是否有权限查看邀请
    const { members } = await getGroupMembers(groupId);
    const currentMember = members.find(m => m.userId === user.id);
    if (!currentMember) {
      return {
        invitations: [],
        error: { message: '您不是该分组的成员' },
      };
    }

    // 修复外键关系查询：使用正确的关系名称
    const { data, error } = await supabase
      .from('group_invitations')
      .select(`
        *,
        invitee:profiles!invitee_id (username, id)
      `)
      .eq('group_id', groupId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[Group] 获取分组邀请失败:', error);
      // 如果外键查询失败，尝试分别查询
      const { data: invitationsData, error: invitationsError } = await supabase
        .from('group_invitations')
        .select('*')
        .eq('group_id', groupId)
        .order('created_at', { ascending: false });

      if (invitationsError) {
        return {
          invitations: [],
          error: {
            message: invitationsError.message || '获取分组邀请失败',
            code: invitationsError.code,
          },
        };
      }

      // 分别查询被邀请者信息
      const inviteeIds = (invitationsData || []).map((item: any) => item.invitee_id);
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, username')
        .in('id', inviteeIds);

      const { data: emailsData } = await supabase
        .rpc('get_user_emails_by_ids', { user_ids: inviteeIds });

      const profileMap = new Map(
        (profilesData || []).map((p: any) => [p.id, p.username])
      );
      const emailMap = new Map<string, string>(
        (emailsData || []).map((e: any) => [e.user_id, e.email || ''])
      );

      const invitations: GroupInvitation[] = (invitationsData || []).map((item: any) => ({
        id: item.id,
        groupId: item.group_id,
        inviterId: item.inviter_id,
        inviteeId: item.invitee_id,
        status: item.status as InvitationStatus,
        createdAt: item.created_at,
        expiresAt: item.expires_at,
        inviteeName: profileMap.get(item.invitee_id) || '',
        inviteeEmail: emailMap.get(item.invitee_id) || '',
      }));

      return { invitations, error: null };
    }

    // 获取被邀请者的邮箱
    const inviteeIds = (data || []).map((item: any) => item.invitee_id);
    const { data: emailsData } = await supabase
      .rpc('get_user_emails_by_ids', { user_ids: inviteeIds });

    const emailMap = new Map<string, string>(
      (emailsData || []).map((e: any) => [e.user_id, e.email || ''])
    );

    const invitations: GroupInvitation[] = (data || []).map((item: any) => ({
      id: item.id,
      groupId: item.group_id,
      inviterId: item.inviter_id,
      inviteeId: item.invitee_id,
      status: item.status as InvitationStatus,
      createdAt: item.created_at,
      expiresAt: item.expires_at,
      inviteeName: item.invitee?.username || item.profiles?.username || '',
      inviteeEmail: emailMap.get(item.invitee_id) || '',
    }));

    return { invitations, error: null };
  } catch (error: any) {
    console.error('[Group] 获取分组邀请异常:', error);
    return {
      invitations: [],
      error: {
        message: error.message || '获取分组邀请时发生未知错误',
      },
    };
  }
}

/**
 * 更新成员角色
 */
export async function updateMemberRole(groupId: string, userId: string, newRole: GroupMemberRole): Promise<{ error: GroupServiceError | null }> {
  if (!supabase) {
    return {
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      error: { message: '用户未登录' },
    };
  }

  try {
    // 检查当前用户权限
    const { members } = await getGroupMembers(groupId);
    const currentMember = members.find(m => m.userId === user.id);
    if (!currentMember) {
      return {
        error: { message: '您不是该分组的成员' },
      };
    }

    // 只有 owner 和 admin 可以修改角色
    if (currentMember.role !== 'owner' && currentMember.role !== 'admin') {
      return {
        error: { message: '只有创建者和管理员可以修改成员角色' },
      };
    }

    // 不能修改创建者的角色
    const targetMember = members.find(m => m.userId === userId);
    if (targetMember?.role === 'owner') {
      return {
        error: { message: '不能修改创建者的角色' },
      };
    }

    // 管理员不能将其他成员设置为 owner
    if (currentMember.role === 'admin' && newRole === 'owner') {
      return {
        error: { message: '只有创建者可以设置其他成员为创建者' },
      };
    }

    // 更新角色
    const { error } = await supabase
      .from('group_members')
      .update({ role: newRole })
      .eq('group_id', groupId)
      .eq('user_id', userId);

    if (error) {
      console.error('[Group] 更新成员角色失败:', error);
      return {
        error: {
          message: error.message || '更新成员角色失败',
          code: error.code,
        },
      };
    }

    // 清除缓存
    await groupCache.clearGroupMembersCache(groupId);

    return { error: null };
  } catch (error: any) {
    console.error('[Group] 更新成员角色异常:', error);
    return {
      error: {
        message: error.message || '更新成员角色时发生未知错误',
      },
    };
  }
}

/**
 * 检查用户是否有特定权限
 */
export async function checkPermission(groupId: string, permission: 'invite' | 'remove' | 'update' | 'delete' | 'manage_role'): Promise<{ hasPermission: boolean; error: GroupServiceError | null }> {
  if (!supabase) {
    return {
      hasPermission: false,
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      hasPermission: false,
      error: { message: '用户未登录' },
    };
  }

  try {
    const { members } = await getGroupMembers(groupId);
    const currentMember = members.find(m => m.userId === user.id);
    if (!currentMember) {
      return {
        hasPermission: false,
        error: { message: '您不是该分组的成员' },
      };
    }

    const role = currentMember.role;

    // 权限检查矩阵
    const permissions: Record<GroupMemberRole, Record<string, boolean>> = {
      owner: {
        invite: true,
        remove: true,
        update: true,
        delete: true,
        manage_role: true,
      },
      admin: {
        invite: true,
        remove: true,
        update: true,
        delete: false,
        manage_role: true,
      },
      member: {
        invite: false,
        remove: false,
        update: false,
        delete: false,
        manage_role: false,
      },
    };

    const hasPermission = permissions[role]?.[permission] || false;

    return { hasPermission, error: null };
  } catch (error: any) {
    console.error('[Group] 检查权限异常:', error);
    return {
      hasPermission: false,
      error: {
        message: error.message || '检查权限时发生未知错误',
      },
    };
  }
}
